import React from 'react';
import { FiStar, FiHeart, FiAward, FiBook, FiUsers, FiUser } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Teachers = () => {
  const teachers = [
    {
      name: "Priya Sharma",
      role: "Lead Educator & Program Director",
      experience: "8+ Years Experience",
      specialization: "Early Childhood Development",
      description: "Passionate about creating nurturing environments where children thrive through play-based learning and individual attention.",
      icon: FiUser,
      iconColor: "from-blue-500 to-indigo-500",
      achievements: ["M.Ed in Early Childhood", "Child Psychology Certified", "Montessori Trained"]
    },
    {
      name: "Kavita Reddy",
      role: "Creative Arts & STEM Coordinator",
      experience: "6+ Years Experience", 
      specialization: "Creative Learning & Robotics",
      description: "Combines artistic creativity with STEM education to inspire young minds and foster innovation through hands-on exploration.",
      icon: FiBook,
      iconColor: "from-emerald-500 to-teal-500",
      achievements: ["B.Ed with Arts Major", "STEM Education Certified", "Robotics Instructor"]
    },
    {
      name: "Anitha Kumar",
      role: "Child Development Specialist",
      experience: "10+ Years Experience",
      specialization: "Social-Emotional Learning",
      description: "Dedicated to supporting each child's emotional growth and building strong foundations for lifelong learning and confidence.",
      icon: FiHeart,
      iconColor: "from-pink-500 to-rose-500",
      achievements: ["Child Psychology Degree", "Special Needs Training", "Parent Counseling Certified"]
    }
  ];

  return (
    <section className="py-20 bg-white" id="teachers">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16" data-aos="fade-up">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our Experienced{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
              Educators
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Meet our passionate team of qualified educators who are dedicated to nurturing 
            your child's growth with expertise, care, and individual attention.
          </p>
        </div>

        {/* Teachers Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {teachers.map((teacher, index) => (
            <div
              key={teacher.name}
              className="group bg-gradient-to-br from-white to-gray-50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
              data-aos="fade-up"
              data-aos-delay={index * 150}
            >
              {/* Icon */}
              <div className="relative mb-6">
                <div className={`w-32 h-32 mx-auto rounded-full bg-gradient-to-br ${teacher.iconColor} flex items-center justify-center ring-4 ring-gray-100 group-hover:ring-gray-200 transition-all duration-300 shadow-lg`}>
                  <SafeIcon icon={teacher.icon} className="w-16 h-16 text-white" />
                </div>
                
                {/* Experience Badge */}
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  {teacher.experience}
                </div>
              </div>

              {/* Content */}
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors duration-200">
                  {teacher.name}
                </h3>
                
                <p className="text-emerald-600 font-semibold mb-2">
                  {teacher.role}
                </p>
                
                <p className="text-gray-500 text-sm mb-4">
                  {teacher.specialization}
                </p>
                
                <p className="text-gray-600 leading-relaxed mb-6">
                  {teacher.description}
                </p>

                {/* Achievements */}
                <div className="space-y-2 mb-6">
                  {teacher.achievements.map((achievement, idx) => (
                    <div key={idx} className="flex items-center justify-center space-x-2 text-sm">
                      <SafeIcon icon={FiAward} className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                      <span className="text-gray-700">{achievement}</span>
                    </div>
                  ))}
                </div>

                {/* Rating */}
                <div className="flex items-center justify-center space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                  <span className="text-sm text-gray-500 ml-2">Parent Rated</span>
                </div>

                {/* Heart Icon */}
                <div className="flex justify-center">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center group-hover:bg-red-200 transition-colors duration-300">
                    <SafeIcon icon={FiHeart} className="w-5 h-5 text-red-500" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Section */}
        <div className="text-center bg-gradient-to-r from-emerald-50 to-teal-50 rounded-3xl p-12" data-aos="fade-up">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Why Parents Trust Our Educators
          </h3>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiAward} className="w-8 h-8 text-emerald-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Qualified & Certified</h4>
              <p className="text-gray-600 text-sm">All educators hold relevant degrees and certifications</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiHeart} className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Caring & Patient</h4>
              <p className="text-gray-600 text-sm">Nurturing approach with individual attention to each child</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiStar} className="w-8 h-8 text-purple-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Continuous Learning</h4>
              <p className="text-gray-600 text-sm">Regular training to stay updated with best practices</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Teachers;